import { Add, Remove } from "@mui/icons-material";
import {
	Autocomplete,
	type AutocompleteProps,
	Box,
	Button,
	ButtonGroup,
	Chip,
	Paper,
	type PaperProps,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { get, type PropertyPath, set } from "lodash";
import type React from "react";
import { TOOLTIP_ENTER_DELAY_MS } from "@/constants";
import { useFilterOptionsQuery } from "../../api";
import { useSearch } from "../../hooks/useSearch";
import { useAppSelector } from "@/redux/hooks";
import type {
	IncludeExcludeMode,
	PropertyStringPath,
	SearchFilterState,
	SelectOptionFieldNamesOfStrings,
} from "../../types";

const EMPTY_ARRAY: readonly never[] = [];

type ACProps = AutocompleteProps<
	string | { label: string },
	true,
	boolean,
	boolean,
	"div"
>;

export interface IncludeExcludeState {
	include: string[];
	exclude: string[];
}

interface Props
	extends Omit<ACProps, "renderInput" | "options" | "value" | "multiple"> {
	label: string;
	fieldName: SelectOptionFieldNamesOfStrings;
	valuesFieldPath: PropertyStringPath<SearchFilterState> & PropertyPath;
	modeFieldPath: PropertyStringPath<SearchFilterState> & PropertyPath;
	onChange?: () => void;
}

interface PaperWithToggleProps extends PaperProps {
	mode?: IncludeExcludeMode;
	ref?: React.Ref<HTMLDivElement>;
}

/** Renders the current inc/exc mode as helper text at the top of the autocomplete dropdown popper. */
function PaperWithMode({
	mode = "include",
	children,
	ref,
	...props
}: PaperWithToggleProps) {
	return (
		<Paper {...props} ref={ref}>
			<Box
				role="status"
				aria-live="polite"
				aria-atomic="true"
				sx={{
					display: "flex",
					justifyContent: "center",
					px: 1,
					py: 0.5,
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Typography
					variant="caption"
					color={mode === "include" ? "success.main" : "error.main"}
				>
					{mode === "include" ? "Including" : "Excluding"}
				</Typography>
			</Box>
			{children}
		</Paper>
	);
}

const sortAsNumber = (a: string, b: string): number => {
	const aInt = Number.parseInt(a, 10);
	const bInt = Number.parseInt(b, 10);
	if ([aInt, bInt].every((n) => typeof n === "number" && !Number.isNaN(n))) {
		return aInt < bInt ? -1 : aInt > bInt ? 1 : 0;
	} else {
		return a.localeCompare(b);
	}
};

/**
 * An autocomplete component that supports include/exclude lists.
 * Selection is stored as a single array in `valuesFieldPath`.
 * The current operation mode ("include" | "exclude") is stored in `modeFieldPath`.
 * The toggle is rendered both inside the dropdown popper and as a button group in the end adornment.
 */
export const FilterIncExcToggle = (props: Props) => {
	const {
		fieldName,
		valuesFieldPath,
		modeFieldPath,
		label,
		onChange: onChangeProp,
		...restProps
	} = props;

	const { data, ...filterRequest } = useFilterOptionsQuery(undefined, {
		selectFromResult: ({ data, ...rest }) => {
			if (!data || !data[fieldName]) return { data: EMPTY_ARRAY, ...rest };

			if (Array.isArray(data[fieldName])) {
				const set = new Set(data[fieldName]);
				return {
					data: Array.from(set).sort(sortAsNumber),
					...rest,
				};
			}

			if (!Array.isArray(data[fieldName])) {
				const values = Object.entries(data[fieldName])
					.map(([key, value]) => ({
						valueCode: key,
						label: `${value}`,
					}))
					.sort((a, b) => (a?.label < b?.label ? -1 : 1));
				return { data: values, ...rest };
			}

			return { data: EMPTY_ARRAY, ...rest };
		},
	});

	const { selectors, updateNext } = useSearch();
	const nextSearchOptions = useAppSelector(
		selectors.selectNextSearchParameters,
	);

	const values: string[] = get(nextSearchOptions, valuesFieldPath) ?? [];
	const mode: IncludeExcludeMode =
		get(nextSearchOptions, modeFieldPath) ?? "include";

	const handleModeChange = (newMode: IncludeExcludeMode) => {
		updateNext(set({}, modeFieldPath, newMode));
	};

	const handleChange: ACProps["onChange"] = (
		_event,
		value,
		_reason,
		_details,
	) => {
		const stringValues = value.map((v) =>
			typeof v === "string" ? v : v.label,
		);
		updateNext(set({}, valuesFieldPath, stringValues));
		onChangeProp?.();
	};

	return (
		<Box sx={{ display: "flex", width: "100%", alignItems: "stretch" }}>
			<Autocomplete
				{...restProps}
				disableCloseOnSelect
				autoHighlight
				fullWidth
				options={data ?? []}
				loading={filterRequest.isLoading}
				multiple
				limitTags={2}
				getLimitTagsText={(more) => `+${more} more`}
				value={values}
				onChange={handleChange}
				slots={{ paper: PaperWithMode }}
				slotProps={{
					paper: { mode } as PaperWithToggleProps,
				}}
				sx={{ flex: 1, minWidth: 0 }}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						sx={{
							"& .MuiOutlinedInput-root": {
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
							},
						}}
						slotProps={{
							input: {
								...params.InputProps,
								endAdornment: params.InputProps.endAdornment,
								"aria-description": `Current mode: ${mode === "include" ? "including" : "excluding"} selected values`,
							},
						}}
					/>
				)}
				renderValue={(tagValue, getTagProps) =>
					tagValue.map((option, index) => {
						const { key, ...chipProps } = getTagProps({ index });
						return (
							<Chip
								variant={mode === "include" ? "filled" : "outlined"}
								key={key}
								{...chipProps}
								label={typeof option === "string" ? option : option.label}
								color={mode === "include" ? "success" : "error"}
							/>
						);
					})
				}
			/>
			<ButtonGroup
				orientation="vertical"
				size="small"
				variant="outlined"
				aria-label={`${label} include or exclude toggle`}
				onMouseDown={(e) => e.preventDefault()}
				sx={{
					ml: "-1px",
					alignSelf: "stretch",
					"& .MuiButtonGroup-grouped": {
						flex: 1,
						borderTopLeftRadius: 0,
						borderBottomLeftRadius: 0,
					},
				}}
			>
				<Tooltip
					title="Include selected values"
					enterDelay={TOOLTIP_ENTER_DELAY_MS}
					placement="top-end"
					arrow
				>
					<Button
						variant={mode === "include" ? "contained" : "outlined"}
						color="success"
						aria-label={`Include — currently ${mode === "include" ? "active" : "inactive"}`}
						aria-pressed={mode === "include"}
						onClick={() => handleModeChange("include")}
						sx={{ minWidth: 0, px: 0.75 }}
					>
						<Add fontSize="small" />
					</Button>
				</Tooltip>
				<Tooltip
					title="Exclude selected values"
					enterDelay={TOOLTIP_ENTER_DELAY_MS}
					placement="top-end"
					arrow
				>
					<Button
						variant={mode === "exclude" ? "contained" : "outlined"}
						color="error"
						aria-label={`Exclude — currently ${mode === "exclude" ? "active" : "inactive"}`}
						aria-pressed={mode === "exclude"}
						onClick={() => handleModeChange("exclude")}
						sx={{ minWidth: 0, px: 0.75 }}
					>
						<Remove fontSize="small" />
					</Button>
				</Tooltip>
			</ButtonGroup>
		</Box>
	);
};
